// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getProp } from "@insite/mobius/utilities";
import { xBaseTheme } from "@sc/XDL/types";
import { color } from "@sc/XDL/Utilities";
import { css } from "styled-components";

interface RadioStyleProps {
    background: string;
    borderColor: string;
}

const radioStyle: {
    width: string;
    height: string;
    borderSize: string;
    default: RadioStyleProps;
    defaultHover: RadioStyleProps;
    checked: RadioStyleProps;
    checkedHover: RadioStyleProps;
} = {
    width: "13px",
    height: "13px",
    borderSize: "1px",
    default: {
        background: "common.ui2",
        borderColor: "common.ui4",
    },
    defaultHover: {
        background: "common.ui2",
        borderColor: "common.ui8",
    },
    checked: {
        background: "common.ui9",
        borderColor: "common.ui4",
    },
    checkedHover: {
        background: "common.ui9",
        borderColor: "common.ui8",
    },
};

export default {
    defaultProps: {
        mergeCss: true,
        color: radioStyle.default.background,
        typographyProps: {
            size: 12,
            css: css`
                letter-spacing: 0;
                line-height: 14px;
            `,
        },
        css: css`
            input {
                & + label {
                    cursor: pointer;
                    transition: all ease-in-out ${getProp("theme.transition.duration.regular")}ms;

                    &::before {
                        width: ${radioStyle.width};
                        height: ${radioStyle.height};
                        background: ${color(radioStyle.default.background)};
                        border: ${radioStyle.borderSize} solid ${color(radioStyle.default.borderColor)};
                        margin-right: 8px;
                    }
                }

                &:disabled + label {
                    cursor: not-allowed;
                    opacity: 0.5;
                }

                &:focus + label::before {
                    box-shadow: none;
                }

                &:focus:not(:disabled) + label::before {
                    border: ${radioStyle.borderSize} solid ${color(radioStyle.defaultHover.borderColor)};
                }

                &:hover:not(:disabled) + label::before {
                    border: ${radioStyle.borderSize} solid ${color(radioStyle.defaultHover.borderColor)};
                }

                &:checked:not(:disabled) + label::before {
                    border: ${radioStyle.borderSize} solid ${color(radioStyle.checked.borderColor)};
                }

                &:checked:hover:not(:disabled) + label::before {
                    border: ${radioStyle.borderSize} solid ${color(radioStyle.checkedHover.borderColor)};
                }

                &:checked + label::after {
                    background: ${color(radioStyle.checked.background)};
                    width: ${radioStyle.width};
                    height: ${radioStyle.height};
                    transform: scale(0.7);
                    left: 0;
                    top: initial;
                }
            }
        `,
    },
    groupDefaultProps: {
        mergeCss: true,
    },
} as xBaseTheme["radio"];
